"use client";
import { jsx, jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from "react";
import { useSafeInView } from "../../hooks/index.js";
import { DEFAULT_IMAGE_URL } from "../../utils/index.js";
import { Skeleton } from "../Skeleton/index.js";
import { image as external_ImageVariants_js_image } from "./ImageVariants.js";
const Image = ({ src = DEFAULT_IMAGE_URL, alt, width, maxWidth, height, maxHeight, ratio, borderRadius, shadow, objectFit, objectPosition, isLoading, className, isSvg, lazyLoad = false, lazyRootMargin = '200px', lazyViewportRef, lazyForceLoad = false, showSkeletonWhileLoading = true, ...props })=>{
    const containerRef = useRef(null);
    const imageRef = useRef(null);
    const isInView = useSafeInView(lazyLoad ? containerRef : imageRef, {
        root: lazyViewportRef,
        margin: lazyRootMargin,
        once: true
    });
    const [shouldLoadImage, setShouldLoadImage] = useState(()=>!lazyLoad || lazyForceLoad);
    const [isImageLoaded, setIsImageLoaded] = useState(false);
    const [imgSrc, setImgSrc] = useState(src || DEFAULT_IMAGE_URL);
    useEffect(()=>{
        if (lazyLoad && (lazyForceLoad || isInView)) setShouldLoadImage(true);
    }, [
        lazyLoad,
        lazyForceLoad,
        isInView
    ]);
    useEffect(()=>{
        setIsImageLoaded(false);
        setImgSrc(src || DEFAULT_IMAGE_URL);
    }, [
        src
    ]);
    const handleImageLoad = useCallback(()=>{
        setIsImageLoaded(true);
    }, []);
    const handleImageError = useCallback(()=>{
        if (imgSrc !== DEFAULT_IMAGE_URL) setImgSrc(DEFAULT_IMAGE_URL);
    }, [
        imgSrc
    ]);
    const classes = external_ImageVariants_js_image({
        width,
        maxWidth,
        height,
        maxHeight,
        ratio,
        borderRadius,
        shadow,
        objectFit,
        objectPosition,
        isLoading,
        className,
        isSvg
    });
    const combinedClasses = ratio ? `${classes} aspect-${ratio}` : classes;
    const showSkeleton = lazyLoad && (!shouldLoadImage || showSkeletonWhileLoading && !isImageLoaded);
    if (showSkeleton) return /*#__PURE__*/ jsxs("div", {
        ref: containerRef,
        className: combinedClasses,
        style: {
            aspectRatio: ratio
        },
        children: [
            /*#__PURE__*/ jsx(Skeleton, {
                type: "image",
                width: "100%",
                height: "100%",
                animate: true
            }),
            shouldLoadImage && /*#__PURE__*/ jsx("img", {
                ref: imageRef,
                src: imgSrc,
                alt: alt,
                onLoad: handleImageLoad,
                onError: handleImageError,
                ...props,
                className: "hidden"
            })
        ]
    });
    return /*#__PURE__*/ jsx("img", {
        ref: imageRef,
        src: imgSrc,
        alt: alt,
        className: combinedClasses,
        onLoad: handleImageLoad,
        onError: handleImageError,
        ...props,
        style: {
            aspectRatio: ratio
        },
        decoding: "async"
    });
};
export { Image };
